/**
 * Labstep
 *
 * @module grid/NewIndex/components/EntityViewLoader
 * @desc Load an entity view based on the category id
 */

import { getDefaultColDefs } from 'labstep-web/grid/NewIndex/coldefs';
import type { ReportingColDef } from 'labstep-web/grid/Report/coldefs/types';
import { ReadOnMountHOC } from 'labstep-web/hoc/ReadOnMount';
import {
  ENTITY_VIEW_DEFAULT_PARAMETERS,
  EntityView,
} from 'labstep-web/models/entity-view.model';
import { LocationService } from 'labstep-web/services/location.service';
import type React from 'react';
import { withRouter } from 'react-router-dom';
import type { GridNewIndexEntityViewLoaderProps } from './types';

const GridNewIndexEntityViewLoader: React.FC<
  GridNewIndexEntityViewLoaderProps
> = ({
  group,
  context,
  entityName,
  location,
  children,
  templateId,
  defaultTemplate,
  premiumFeatures,
  ...rest
}) => {
  const paramTemplateId =
    templateId ||
    LocationService.getQueryParameter(location, 'template_id');

  const columnDefs = getDefaultColDefs(
    context,
    entityName,
    defaultTemplate,
    premiumFeatures,
  );

  const defaultEntityView = new EntityView({
    group,
    context:
      context.includes('_index') && !paramTemplateId
        ? `${context}_all`
        : context,
    entity_name: entityName,
    templateId: paramTemplateId,
    column_definition_ids: columnDefs.map(
      (colDef: ReportingColDef) => colDef.colId || '',
    ),
    parameters: ENTITY_VIEW_DEFAULT_PARAMETERS,
    column_options: {
      name: {
        showMetadataPreview: false,
      },
      resource: {
        showMetadataPreview: false,
      },
      status: {
        showLog: false,
      },
    },
  });
  if (context === 'order_request_index') {
    defaultEntityView.column_options.name.showMetadataPreview = true;
    defaultEntityView.column_options.resource.showMetadataPreview = true;
    defaultEntityView.column_options.status.showLog = true;
  }

  const params: Record<string, unknown> = {
    group_id: group.guid,
    context:
      context.includes('_index') && !paramTemplateId
        ? `${context}_all`
        : context,
    entity_name: entityName,
  };
  if (paramTemplateId && paramTemplateId !== 'none') {
    params[`${entityName}_template_id`] = paramTemplateId;
  } else {
    params.has_template = 0;
  }

  return (
    <ReadOnMountHOC
      key={paramTemplateId}
      type="cursor"
      entityName={EntityView.entityName}
      params={params}
      loading={false}
      children={({ entities }) => {
        const entityView = entities.length > 0 ? entities[0] : null;
        if (entityView && entityName !== entityView.entity_name) {
          return null;
        }
        return children({
          entityView:
            entityView && !entityView.deleted_at
              ? entityView
              : defaultEntityView,
          ...rest,
        });
      }}
    />
  );
};

export default withRouter(GridNewIndexEntityViewLoader);
